import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import './InStoreModeEnhanced.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InStoreModeEnhanced = () => {
  const navigate = useNavigate();
  const { sessionToken, user } = useAuth();
  
  // State management
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerPhoto, setCustomerPhoto] = useState(null);
  const [customerPhotoPreview, setCustomerPhotoPreview] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1); // 1: select brand, 2: add products, 3: take photo, 4: results
  
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (!user?.is_merchant) {
      navigate('/');
      return;
    }
    loadBrands();
  }, []);

  useEffect(() => {
    if (searchQuery && selectedBrand && step === 2) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        searchProducts();
      }, 300);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, selectedBrand]);

  const loadBrands = async () => {
    try {
      const response = await axios.get(`${API}/merchant/brands`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      if (response.data.success) {
        setBrands(response.data.brands);
        if (response.data.brands.length > 0) {
          setSelectedBrand(response.data.brands[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading brands:', error);
      setError('Failed to load brands');
    }
  };

  const searchProducts = async () => {
    try {
      const response = await axios.get(
        `${API}/instore/products/search?brand_id=${selectedBrand}&query=${searchQuery}`,
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );

      if (response.data.success) {
        setSearchResults(response.data.products);
      }
    } catch (error) {
      console.error('Error searching products:', error);
    }
  };

  const addToCart = (product) => {
    if (cart.length >= 4) {
      setError('Maximum 4 items allowed');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    if (cart.find(p => p.id === product.id)) {
      setError('Product already in cart');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setCart([...cart, product]);
    setSearchQuery('');
    setSearchResults([]);
    setSuccess('Product added to cart!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(p => p.id !== productId));
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setCustomerPhoto(reader.result);
      setCustomerPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (!customerPhoto) {
      setError('Please capture customer photo');
      return;
    }

    if (cart.length === 0) {
      setError('Please add at least one product');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await axios.post(
        `${API}/instore/tryon`,
        {
          brand_id: selectedBrand,
          customer_photo_base64: customerPhoto,
          product_ids: cart.map(p => p.id),
          background_style: 'studio'
        },
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );

      if (response.data.success) {
        setGeneratedImages(response.data.images);
        setCurrentImageIndex(0);
        setStep(4);
        setSuccess(`Generated ${response.data.total_images} try-on images!`);
      }
    } catch (error) {
      console.error('Error generating try-on:', error);
      setError(error.response?.data?.detail || 'Failed to generate try-on');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (image) => {
    const link = document.createElement('a');
    link.href = image.result_image;
    link.download = `tryon-${image.product_name}-${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadAll = () => {
    generatedImages.forEach((image, index) => {
      setTimeout(() => handleDownload(image), index * 500);
    });
  };

  const handleShareWhatsApp = (image) => {
    const text = encodeURIComponent(`Check out this ${image.product_name}! Virtual Try-On by FITTTY.AI`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleEmail = async (image) => {
    const subject = encodeURIComponent(`Virtual Try-On: ${image.product_name}`);
    const body = encodeURIComponent(`Here's the virtual try-on result for ${image.product_name}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleReject = (imageIndex) => {
    setGeneratedImages(prev => prev.filter((_, idx) => idx !== imageIndex));
    if (generatedImages.length === 1) {
      setStep(3);
      setGeneratedImages([]);
    } else if (currentImageIndex >= generatedImages.length - 1) {
      setCurrentImageIndex(Math.max(0, generatedImages.length - 2));
    }
  };

  const startOver = () => {
    setCart([]);
    setCustomerPhoto(null);
    setCustomerPhotoPreview(null);
    setGeneratedImages([]);
    setCurrentImageIndex(0);
    setStep(2);
    setSearchQuery('');
    setError('');
    setSuccess('');
  };

  const brandName = brands.find(b => b.id === selectedBrand)?.name || '';

  return (
    <Layout>
      <div className="instore-enhanced">
        <div className="instore-header">
          <button className="back-btn" onClick={() => navigate('/merchant')}>
            ← Dashboard
          </button>
          <div>
            <h1>🏬 In-Store Experience</h1>
            <p className="brand-name">{brandName}</p>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Step 1: Brand Selection */}
        {step === 1 && (
          <div className="step-container">
            <h2>Select Brand</h2>
            <div className="brand-grid">
              {brands.map(brand => (
                <div
                  key={brand.id}
                  className={`brand-card ${selectedBrand === brand.id ? 'selected' : ''}`}
                  onClick={() => setSelectedBrand(brand.id)}
                >
                  <h3>{brand.name}</h3>
                  <p>{brand.product_count || 0} products</p>
                </div>
              ))}
            </div>
            <button 
              className="btn-primary btn-large"
              onClick={() => setStep(2)}
              disabled={!selectedBrand}
            >
              Continue →
            </button>
          </div>
        )}

        {/* Step 2: Add Products */}
        {step === 2 && (
          <div className="step-container">
            <div className="products-section">
              <div className="search-section">
                <h2>Search Products</h2>
                <div className="search-bar">
                  <input
                    type="text"
                    placeholder="Search by name, serial, or scan barcode..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <button className="btn-scan">📷 Scan</button>
                </div>

                {searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map(product => (
                      <div key={product.id} className="search-result-item">
                        <img src={product.picture_link} alt={product.product_name} />
                        <div className="product-info">
                          <h4>{product.product_name}</h4>
                          <p className="price">${product.price}</p>
                        </div>
                        <button 
                          className="btn-add"
                          onClick={() => addToCart(product)}
                        >
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="cart-section">
                <h2>Selected Items ({cart.length}/4)</h2>
                {cart.length === 0 ? (
                  <div className="empty-cart">
                    <p>🛒 No items selected</p>
                    <p className="hint">Search and add 3-4 products</p>
                  </div>
                ) : (
                  <div className="cart-items">
                    {cart.map(product => (
                      <div key={product.id} className="cart-item">
                        <img src={product.picture_link} alt={product.product_name} />
                        <div className="cart-item-info">
                          <h4>{product.product_name}</h4>
                          <p>${product.price}</p>
                        </div>
                        <button 
                          className="btn-remove"
                          onClick={() => removeFromCart(product.id)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button 
                  className="btn-primary btn-large"
                  onClick={() => setStep(3)}
                  disabled={cart.length === 0}
                >
                  Continue to Photo →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Customer Photo */}
        {step === 3 && (
          <div className="step-container">
            <h2>📸 Capture Customer Photo</h2>
            
            <div className="photo-capture-section">
              {customerPhotoPreview ? (
                <div className="photo-preview">
                  <img src={customerPhotoPreview} alt="Customer" />
                  <button 
                    className="btn-change"
                    onClick={() => {
                      setCustomerPhoto(null);
                      setCustomerPhotoPreview(null);
                    }}
                  >
                    Change Photo
                  </button>
                </div>
              ) : (
                <label className="photo-upload">
                  <input
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={handlePhotoCapture}
                    style={{ display: 'none' }}
                  />
                  <div className="upload-prompt">
                    <span className="upload-icon">📷</span>
                    <h3>Take or Upload Photo</h3>
                    <p>Full body photo works best</p>
                  </div>
                </label>
              )}
            </div>

            <div className="action-buttons">
              <button className="btn-secondary" onClick={() => setStep(2)}>
                ← Back to Products
              </button>
              <button 
                className="btn-primary btn-large"
                onClick={handleGenerate}
                disabled={!customerPhoto || loading}
              >
                {loading ? 'Generating...' : `Generate ${cart.length} Try-Ons`}
              </button>
            </div>

            {loading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Generating virtual try-ons...</p>
                <p className="loading-hint">This may take 30-60 seconds</p>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Results Gallery */}
        {step === 4 && generatedImages.length > 0 && (
          <div className="results-container">
            <h2>✨ Try-On Results</h2>
            
            <div className="gallery-view">
              <div className="main-image-container">
                <img 
                  src={generatedImages[currentImageIndex].result_image} 
                  alt="Try-on result"
                  className="main-result-image"
                />
                <div className="image-info">
                  <h3>{generatedImages[currentImageIndex].product_name}</h3>
                  <p className="brand-price">
                    {generatedImages[currentImageIndex].product_brand} • 
                    ${generatedImages[currentImageIndex].product_price}
                  </p>
                </div>
              </div>

              <div className="gallery-nav">
                {generatedImages.map((img, idx) => (
                  <div
                    key={idx}
                    className={`thumbnail ${idx === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(idx)}
                  >
                    <img src={img.result_image} alt={`Result ${idx + 1}`} />
                    <span className="thumbnail-label">{idx + 1}</span>
                  </div>
                ))}
              </div>

              <div className="image-actions">
                <button 
                  className="action-btn whatsapp"
                  onClick={() => handleShareWhatsApp(generatedImages[currentImageIndex])}
                >
                  💬 WhatsApp
                </button>
                <button 
                  className="action-btn email"
                  onClick={() => handleEmail(generatedImages[currentImageIndex])}
                >
                  📧 Email
                </button>
                <button 
                  className="action-btn download"
                  onClick={() => handleDownload(generatedImages[currentImageIndex])}
                >
                  ⬇️ Download
                </button>
                <button 
                  className="action-btn reject"
                  onClick={() => handleReject(currentImageIndex)}
                >
                  🗑️ Reject
                </button>
              </div>

              <div className="bulk-actions">
                <button className="btn-secondary" onClick={handleDownloadAll}>
                  ⬇️ Download All ({generatedImages.length})
                </button>
                <button className="btn-secondary" onClick={() => setStep(3)}>
                  🔄 Generate Again
                </button>
                <button className="btn-primary" onClick={startOver}>
                  ✨ New Customer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InStoreModeEnhanced;
